import React from 'react';

import { Order } from '../types';

interface OrderDetailsModalProps {
  order: Order;
  editedOrder: Partial<Order>;
  isEditing: boolean;
  onClose: () => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSave: () => void;
  onEditChange: (field: string, value: any) => void;
  onUpdateStatus: (orderId: string, newStatus: string) => void;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  editedOrder,
  isEditing,
  onClose,
  onStartEditing,
  onCancelEditing,
  onSave,
  onEditChange,
  onUpdateStatus
}) => {

  const getStatusColor = (status: string) => {
    const colors = {
      'pending': { background: '#fff3cd', color: '#856404' },
      'assigned': { background: '#d4edda', color: '#155724' },
      'planted': { background: '#d1ecf1', color: '#0c5460' },
      'completed': { background: '#d1e7dd', color: '#0a3622' }
    };
    return colors[status as keyof typeof colors] || { background: '#e9ecef', color: '#495057' };
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
          color: 'white',
          borderRadius: '15px 15px 0 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>
              {isEditing ? 'Editando Orden' : 'Detalles de la Orden'} - #{order._id.slice(-6)}
            </h2>
            {isEditing && (
              <span style={{
                fontSize: '0.9rem',
                background: 'rgba(255,255,255,0.2)',
                padding: '4px 8px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.3)'
              }}>
                {'Modo de Edición'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {!isEditing && (
              <button
                onClick={onStartEditing}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {'Editar'}
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={onSave}
                  style={{
                    background: 'rgba(40, 167, 69, 0.9)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  {'Guardar'}
                </button>
                <button
                  onClick={onCancelEditing}
                  style={{
                    background: 'rgba(108, 117, 125, 0.9)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  {'Cancelar'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Status and Basic Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div>
              <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {'Estado'}
              </div>
              <span style={{
                padding: '5px 15px',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: '600',
                ...getStatusColor(order.status)
              }}>
                {({'pending':'Pendiente','assigned':'Asignado','planted':'Plantado','completed':'Completado'} as Record<string,string>)[order.status] || order.status}
              </span>
            </div>

            <div>
              <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {'Árboles'}
              </div>
              {isEditing ? (
                <input
                  type="number"
                  value={editedOrder.trees || editedOrder.treesOrdered || ''}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    onEditChange('trees', value);
                    onEditChange('treesOrdered', value);
                  }}
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#228B22',
                    border: '2px solid #228B22',
                    borderRadius: '5px',
                    padding: '4px 8px',
                    width: '100px',
                    background: 'white'
                  }}
                />
              ) : (
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#228B22' }}>
                  {order.trees || order.treesOrdered || 0} trees
                </div>
              )}
            </div>

            <div>
              <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {'Cantidad Total'}
              </div>
              {isEditing ? (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <select
                    value={editedOrder.currency || order.currency || 'USD'}
                    onChange={(e) => onEditChange('currency', e.target.value)}
                    style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      border: '2px solid #666',
                      borderRadius: '5px',
                      padding: '4px 8px',
                      background: 'white'
                    }}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                    <option value="COP">COP</option>
                    <option value="MXN">MXN</option>
                    <option value="BRL">BRL</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    value={((editedOrder.totalAmount || editedOrder.amount || 0) / 100).toFixed(2)}
                    onChange={(e) => {
                      const dollarValue = parseFloat(e.target.value) || 0;
                      const centValue = Math.round(dollarValue * 100);
                      onEditChange('totalAmount', centValue);
                      onEditChange('amount', centValue);
                    }}
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#333',
                      border: '2px solid #666',
                      borderRadius: '5px',
                      padding: '4px 8px',
                      width: '120px',
                      background: 'white'
                    }}
                  />
                </div>
              ) : (
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>
                  {order.currency || 'USD'} {((order.totalAmount || order.amount || 0) / 100).toFixed(2)}
                </div>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
              {'Información del Cliente'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                  {'Correo'}
                </div>
                {isEditing ? (
                  <input
                    type="email"
                    value={editedOrder.customerEmail || ''}
                    onChange={(e) => onEditChange('customerEmail', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      border: '2px solid #666',
                      borderRadius: '5px',
                      fontSize: '1rem',
                      marginTop: '0.2rem'
                    }}
                  />
                ) : (
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>
                    {order.customerEmail}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                  {'Nombre'}
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedOrder.customerName || ''}
                    onChange={(e) => onEditChange('customerName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      border: '2px solid #666',
                      borderRadius: '5px',
                      fontSize: '1rem',
                      marginTop: '0.2rem'
                    }}
                    placeholder="Customer name"
                  />
                ) : (
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>
                    {order.customerName || 'Not provided'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
              {'Cronología de la Orden'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#228B22',
                  flexShrink: 0
                }}></div>
                <div>
                  <strong>{'Pedido'}:</strong> {new Date(order.createdAt).toLocaleString()}
                </div>
              </div>
              {order.assignedDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: order.status === 'assigned' || order.status === 'planted' || order.status === 'completed' ? '#228B22' : '#ccc',
                    flexShrink: 0
                  }}></div>
                  <div>
                    <strong>{'Asignado'}:</strong> {new Date(order.assignedDate).toLocaleString()}
                  </div>
                </div>
              )}
              {order.plantingDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: order.status === 'planted' || order.status === 'completed' ? '#228B22' : '#ccc',
                    flexShrink: 0
                  }}></div>
                  <div>
                    <strong>{'Plantado'}:</strong> {new Date(order.plantingDate).toLocaleString()}
                  </div>
                </div>
              )}
              {order.completionDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: order.status === 'completed' ? '#228B22' : '#ccc',
                    flexShrink: 0
                  }}></div>
                  <div>
                    <strong>{'Completado'}:</strong> {new Date(order.completionDate).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Worker Information */}
          {order.assignedWorker && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
                {'Trabajador Asignado'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                    {'Nombre'}
                  </div>
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>
                    {order.assignedWorker.name}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                    {'Correo'}
                  </div>
                  <div style={{ color: '#333', marginTop: '0.2rem' }}>
                    {order.assignedWorker.email}
                  </div>
                </div>
                {order.assignedWorker.region && (
                  <div>
                    <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                      {'Región'}
                    </div>
                    <div style={{ color: '#333', marginTop: '0.2rem' }}>
                      {order.assignedWorker.region}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location Information */}
          {(order.location || order.coordinates || isEditing) && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
                {'Ubicación de Plantación'}
              </h3>
              {isEditing ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      Location Description
                    </label>
                    <textarea
                      value={editedOrder.location || ''}
                      onChange={(e) => onEditChange('location', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #666',
                        borderRadius: '5px',
                        fontSize: '1rem',
                        resize: 'vertical',
                        minHeight: '60px',
                        fontFamily: 'inherit'
                      }}
                      placeholder="Enter planting location description"
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                        Latitude
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={editedOrder.coordinates?.latitude || ''}
                        onChange={(e) => onEditChange('coordinates', {
                          ...(editedOrder.coordinates || order.coordinates || {}),
                          latitude: parseFloat(e.target.value) || 0
                        })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '2px solid #666',
                          borderRadius: '5px',
                          fontSize: '0.9rem'
                        }}
                        placeholder="e.g. 40.712776"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                        Longitude
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={editedOrder.coordinates?.longitude || ''}
                        onChange={(e) => onEditChange('coordinates', {
                          ...(editedOrder.coordinates || order.coordinates || {}),
                          longitude: parseFloat(e.target.value) || 0
                        })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '2px solid #666',
                          borderRadius: '5px',
                          fontSize: '0.9rem'
                        }}
                        placeholder="e.g. -74.005974"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {order.location && (
                    <div style={{ color: '#333', marginBottom: '0.5rem' }}>
                      {order.location}
                    </div>
                  )}
                  {order.coordinates && (
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      <strong>{'Coordenadas'}:</strong> {order.coordinates.latitude.toFixed(6)}, {order.coordinates.longitude.toFixed(6)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Photos */}
          {order.photos && order.photos.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
                {'Fotos del Árbol'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem' }}>
                {order.photos.map((photo, index) => {
                  const photoFilename = typeof photo === 'string' ? photo : (photo as any).filename || photo;
                  return (
                    <div key={index} style={{ textAlign: 'center' }}>
                      <img
                        src={`/api/photos/${photoFilename}`}
                        alt={`Tree ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '2px solid #eee'
                        }}
                        onClick={() => window.open(`/api/photos/${photoFilename}`, '_blank')}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.3rem' }}>
                        Photo {index + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          {(order.notes || isEditing) && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333', borderBottom: '2px solid #228B22', paddingBottom: '0.5rem' }}>
                {'Notas'}
              </h3>
              {isEditing ? (
                <textarea
                  value={editedOrder.notes || ''}
                  onChange={(e) => onEditChange('notes', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #666',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical',
                    minHeight: '100px',
                    fontFamily: 'inherit',
                    backgroundColor: '#f8f9fa'
                  }}
                  placeholder="Add notes about this order..."
                />
              ) : (
                <div style={{
                  color: '#333',
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  {order.notes || 'No notes'}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            borderTop: '1px solid #eee',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontWeight: '600', color: '#666' }}>
                {'Cambiar Estado'}:
              </label>
              <select
                value={order.status}
                onChange={(e) => {
                  onUpdateStatus(order._id, e.target.value);
                  onClose();
                }}
                style={{
                  padding: '8px 12px',
                  border: '2px solid #228B22',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#333',
                  background: '#fff'
                }}
              >
                <option value="pending">{'Pendiente'}</option>
                <option value="assigned">{'Asignado'}</option>
                <option value="planted">{'Plantado'}</option>
                <option value="completed">{'Completado'}</option>
              </select>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              {'Cerrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
